import {
    Box,
    Button,
    CircularProgress,
    FormControl,
} from "@mui/material";
import {useEffect, useState} from "react";
import {Create} from "react-admin";
import {useLocation, useNavigate} from "react-router-dom"
import ChefForPartyService from "../../services/ChefForPartyService";
import {ChefForPartyResponse} from "../../interfaces/ChefForParty/ChefForPartyResponse";
import EventBus from "../../EventBus";
import {EventBusEventsEnum} from "../../enums/EventBusEventsEnum";
import {CustomSnackbarTypes} from "../Common/CustomSnackBar";
import { ChefForPartyBookingTypeEnum } from "../../enums/ChefForPartyBookingTypeEnum";
import BookByDishEditForm from "../BookByDish/BookByDishEditForm";
import {
    BlueTOpBorderContainer,
    FilledButton,
    FormFieldGroup,
    FormGrid,
    FormLabel,
    SectionTitleRow,
    StyledDateText,
    WorkspaceContainer,
    WorkspaceHeader,
    WorkspaceIconBadge,
    WorkspacePanel,
    WorkspaceTitle,
} from "../../styles/commonStyles";
import { EditIcon } from "../Svg";

const chefForPartyService = new ChefForPartyService();

const ChefForPartyEdit = () => {
    const location = useLocation();
    // Served under both /ChefForParty/:id and /BookByDish/:id — keep navigation
    // within whichever module the user came from.
    const resource = location.pathname.split('/')[1]
    const chefForPartyId = location.pathname.split('/')[2]
    const [houseId, setHouseId] = useState<any>(null);
    const [date, setDate] = useState<any>(null);
    const [noOfAdults, setNoOfAdults] = useState(0);
    const [noOfChildren, setNoOfChildren] = useState(0);
    const [noOfDishes, setNoOfDishes] = useState(0);
    const [noOfStarters, setNoOfStarters] = useState(0);
    const [noOfMainCourse, setNoOfMainCourse] = useState(0);
    const [noOfDesserts, setNoOfDesserts] = useState(0);
    const [noOfSideDishes, setNoOfSideDishes] = useState(0);
    const [noOfBeverages, setNoOfBeverages] = useState(0);
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [bartender, setBartender] = useState<number>(0);
    const [waiter, setWaiter] = useState<number>(0);
    const [party, setParty] = useState<any>(null);
    const [calculating, setCalculating] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const navigate = useNavigate();

    const calculatePrice = () => {
        let addOns = [];
        if(bartender > 0) {
            addOns.push({"addOnId": 1,"quantity": bartender,"duration": 0})
        }
        if(waiter > 0) {
            addOns.push({"addOnId": 2,"quantity": waiter,"duration": 0})
        }
        let dishesCountObj: any = {};
        dishesCountObj["noOfStarters"] = noOfStarters
        dishesCountObj["noOfMainCourse"] = noOfMainCourse
        dishesCountObj["noOfDesserts"] = noOfDesserts
        dishesCountObj["noOfSideDishes"] = noOfSideDishes
        dishesCountObj["noOfBeverages"] = noOfBeverages
        if (noOfDishes !== 0) {
            setCalculating(true);
            chefForPartyService
                .calculateDynamicPrice({
                    date,
                    houseId,
                    noOfAdults: Number(noOfAdults),
                    noOfChildren: Number(noOfChildren),
                    noOfDishes: Number(noOfDishes),
                    addOns,
                    dishesCountObj,
                })
                .then((res: any) => {
                    if (res?.totalAmount) {
                        setTotalPrice(Number(res.totalAmount));
                    }
                })
                .finally(() => setCalculating(false));
        }
    };

    const loadParty = () => {
        return chefForPartyService.fetchOneChefForParty(Number(chefForPartyId)).then((res) => {
            setParty(res?.data);
            const {
                houseId,
                date,
                noOfAdults,
                noOfChildren,
                noOfDesserts,
                noOfDishes,
                noOfMainCourse,
                noOfSideDishes,
                noOfBeverages,
                noOfStarters,
                orders,
            } = res?.data;
            setNoOfAdults(noOfAdults);
            setHouseId(houseId);
            setDate(date);
            setNoOfChildren(noOfChildren);
            setNoOfDishes(noOfDishes);
            setNoOfStarters(noOfStarters);
            setNoOfMainCourse(noOfMainCourse);
            setNoOfDesserts(noOfDesserts);
            setNoOfSideDishes(noOfSideDishes);
            setNoOfBeverages(noOfBeverages);
            orders.map((eachOrder: any) => {
                if(eachOrder?.orderItems?.length !== 0){
                    eachOrder?.orderItems.map((eachOrderItem: any) => {
                        if(eachOrderItem?.serviceAddons?.id === 1){
                            setBartender((prev) => prev + Number(eachOrderItem?.quantity));
                        }
                        if(eachOrderItem?.serviceAddons?.id === 2){
                            setWaiter((prev) => prev + Number(eachOrderItem?.quantity));
                        }
                        return eachOrderItem;
                    })
                }
                return eachOrder;
            })
        });
    };

    useEffect(() => {
        if(!chefForPartyId) return;
        loadParty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chefForPartyId]);

    if (!party) {
        return (
            <Create title={"Edit"}>
                <Box style={{ padding: 24 }}>
                    <CircularProgress />
                </Box>
            </Create>
        );
    }

    if (party?.bookingType === ChefForPartyBookingTypeEnum.BOOK_BY_DISH) {
        return (
            <Create title={"Edit Book by Dish"}>
                <BookByDishEditForm partyId={Number(chefForPartyId)} party={party} onRefreshParty={loadParty} />
            </Create>
        );
    }

    const handleSubmit = () => {
        let addOns = [];
        addOns.push({"addOnId": 1,"quantity": bartender,"duration": 0})
        addOns.push({"addOnId": 2,"quantity": waiter,"duration": 0})
        setSubmitting(true);
        chefForPartyService
            .editChefForPartyService(Number(chefForPartyId), {
                noOfAdults: Number(noOfAdults),
                noOfChildren: Number(noOfChildren),
                noOfDishes: Number(noOfDishes),
                noOfStarters: Number(noOfStarters),
                noOfMainCourse: Number(noOfMainCourse),
                noOfDesserts: Number(noOfDesserts),
                addOns,
                noOfSideDishes: Number(noOfSideDishes),
                noOfBeverages: Number(noOfBeverages),
            })
            .then((data: ChefForPartyResponse) => {
                if (data?.id) {
                    navigate(`/${resource}/${data?.id}/show`, { replace: true });
                    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                        message: "Edited Successfully",
                        type: CustomSnackbarTypes.SUCCESS,
                    });
                }
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Create title={"Edit Chef for party"}>
            <WorkspaceContainer>
                {/* Header */}
                <WorkspaceHeader>
                    <SectionTitleRow>
                        <WorkspaceIconBadge>
                            <EditIcon />
                        </WorkspaceIconBadge>
                        <WorkspaceTitle>Edit Chef for Party #{chefForPartyId}</WorkspaceTitle>
                    </SectionTitleRow>
                </WorkspaceHeader>

                {/* Form */}
                <WorkspacePanel>
                    <FormGrid>
                        <FormFieldGroup>
                            <FormLabel>No of Adults</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={noOfAdults}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    onChange={(e: any) => { setNoOfAdults(e.target.value); setTotalPrice(0); }}
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>

                        <FormFieldGroup>
                            <FormLabel>No of Children</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={noOfChildren}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    onChange={(e: any) => { setNoOfChildren(e.target.value); setTotalPrice(0); }}
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>

                        <FormFieldGroup>
                            <FormLabel>No of Bartender</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={bartender}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    onChange={(e: any) => { setBartender(e.target.value); setTotalPrice(0); }}
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>

                        <FormFieldGroup>
                            <FormLabel>No of Waiter</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={waiter}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    onChange={(e: any) => { setWaiter(e.target.value); setTotalPrice(0); }}
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>

                        <FormFieldGroup>
                            <FormLabel>No of Dishes (auto)</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={noOfDishes}
                                    disabled
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>

                        <FormFieldGroup>
                            <FormLabel>No of Starters</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={noOfStarters}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    onChange={(e: any) => {
                                        setNoOfDishes(Number(noOfSideDishes) + Number(noOfBeverages) + Number(noOfDesserts) + Number(noOfMainCourse) + Number(e.target.value));
                                        setNoOfStarters(e.target.value);
                                        setTotalPrice(0);
                                    }}
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>

                        <FormFieldGroup>
                            <FormLabel>No of Main Course</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={noOfMainCourse}
                                    InputProps={{ inputProps: { min: 1 } }}
                                    onChange={(e: any) => {
                                        setNoOfDishes(Number(noOfSideDishes) + Number(noOfBeverages) + Number(noOfDesserts) + Number(e.target.value) + Number(noOfStarters));
                                        setNoOfMainCourse(e.target.value);
                                        setTotalPrice(0);
                                    }}
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>

                        <FormFieldGroup>
                            <FormLabel>No of Desserts</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={noOfDesserts}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    onChange={(e: any) => {
                                        setNoOfDishes(Number(noOfSideDishes) + Number(noOfBeverages) + Number(e.target.value) + Number(noOfMainCourse) + Number(noOfStarters));
                                        setNoOfDesserts(e.target.value);
                                        setTotalPrice(0);
                                    }}
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>

                        <FormFieldGroup>
                            <FormLabel>No of Side Dishes</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={noOfSideDishes}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    onChange={(e: any) => {
                                        setNoOfDishes(Number(e.target.value) + Number(noOfBeverages) + Number(noOfDesserts) + Number(noOfMainCourse) + Number(noOfStarters));
                                        setNoOfSideDishes(e.target.value);
                                        setTotalPrice(0);
                                    }}
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>

                        <FormFieldGroup>
                            <FormLabel>No of Beverages</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={noOfBeverages}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    onChange={(e: any) => {
                                        setNoOfDishes(Number(e.target.value) + Number(noOfSideDishes) + Number(noOfDesserts) + Number(noOfMainCourse) + Number(noOfStarters));
                                        setNoOfBeverages(e.target.value);
                                        setTotalPrice(0);
                                    }}
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>

                        <FormFieldGroup>
                            <FormLabel>Total Price (₹)</FormLabel>
                            <FormControl style={{ width: "100%" }}>
                                <StyledDateText
                                    type="number"
                                    value={totalPrice}
                                    disabled
                                    variant="outlined"
                                    label=""
                                />
                            </FormControl>
                        </FormFieldGroup>
                    </FormGrid>

                    {/* Actions */}
                    <BlueTOpBorderContainer>
                        <FilledButton onClick={calculatePrice} disabled={calculating} style={{ minWidth: 140 }}>
                            {calculating && <CircularProgress size={14} sx={{ mr: 1, color: '#fff' }} />}
                            Calculate Price
                        </FilledButton>
                        <Button
                            variant="contained"
                            color="success"
                            size="large"
                            disabled={totalPrice === 0 || submitting}
                            onClick={handleSubmit}
                            sx={{ textTransform: "none", boxShadow: "none" }}
                        >
                            {submitting ? "Saving…" : "Submit"}
                        </Button>
                    </BlueTOpBorderContainer>
                </WorkspacePanel>
            </WorkspaceContainer>
        </Create>
    );
};

export default ChefForPartyEdit;
